import { DecimalPipe } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ControlContainer, FormGroupDirective, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { SelectBox, TimeApiService } from 'cbc-shared-lib';
import { Subject } from 'rxjs';
import {
  CodeConversionMstDto, Sgke341aResponse, Sgke341aResponseClmtoInfo,
  Sgke341aResponseShishinInputCont, Sgke341aResponseSupplystopResultCont, Sgke341aResponseSuppntInfo
} from 'sg-api';

@Component({
  selector: 'app-sgke340d-condition',
  templateUrl: './sgke340d-condition.component.html',
  styleUrl: './sgke340d-condition.component.scss',
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  providers: [DecimalPipe]
})
export class Sgke340dConditionComponent implements OnInit, OnDestroy {

  /** コード変換マスタマップ */
  @Input() codeMap: Map<string, CodeConversionMstDto[]> = null;
  /** 初期表示情報（初期表示APIレスポンス）*/
  @Input() initResponse: Sgke341aResponse;

  /** 指針入力ボタンクリックEventEmitter */
  @Output() onClickShishinButtonEvent = new EventEmitter();
  /** ボタンクリックEventEmitter */
  @Output() onClickButtonEvent = new EventEmitter();

  /** メーター工事要否コード_否 */
  private static METER_CONST_YHICODE_HI = '0';
  /** メーター分類コード_マイコンメーター */
  private static METER_BUNRUI_CODE_MCU = '02';

  /** 供給停止再開結果区分コード_供給停止 */
  private static SUPPLY_STOP_RESTART_RESULT_CODE_STOP = '0';

  /** マイコンメーター点滅フラグ_なし */
  private static MICROCOMPUTER_METER_BLINK_FLAG_OFF = '0';

  /** 簡易供給再開 */
  private static QUICK_RESUME = '簡易供給再開';
  /** 簡易供給再開(連絡要) */
  private static QUICK_RESUME_NEED_CONTACT = '簡易供給再開（連絡要）';
  /** 一般供給再開 */
  private static SUPPLY_RESUME = '一般供給再開';

  /** 購読を停止するためのSubject */
  private onDestroy$ = new Subject();
  /** 親フォーム情報 */
  private parentForm: UntypedFormGroup;
  /** 登録APIリクエスト FormGroup */
  regInfFormGroup: UntypedFormGroup;
  /** 供給地点情報 */
  suppntInfo: Sgke341aResponseSuppntInfo;
  /** 請求先情報 */
  clmtoInfo: Sgke341aResponseClmtoInfo;
  /** 供給停止結果内容 */
  supplystopResultCont: Sgke341aResponseSupplystopResultCont;
  /** 指針入力内容 */
  shishinInputCont: Sgke341aResponseShishinInputCont;

  /** マイコン点滅コードリスト(S301191) */
  microcontrollermeterFlashingList: SelectBox[] = [];
  /** エフ取付コードリスト(S301231) */
  efuInstallationList: SelectBox[] = [];
  /** 作業メモ表示フラグ */
  isSgyshMemoVisible: boolean = false;

  /**
   * 使用者名取得
   * @public
   * @returns {string}
   * @memberof Sgke340dConditionComponent
   */
  public get siyoushamei(): string {
    return `${this.suppntInfo.userMeiKanji}（${this.suppntInfo.userMeiKana}）`;
  }

  /**
   * 請求先名取得
   * @returns {string}
   * @memberof Sgke340dConditionComponent
   */
  public get seikyusakimei(): string {
    // "請求先名カナ"がある場合はカナも表示する
    if (this.clmtoInfo.clmtoMeiKana) {
      return `${this.clmtoInfo.clmtoMeiKanji}（${this.clmtoInfo.clmtoMeiKana}）`;
    } else {
      return `${this.clmtoInfo.clmtoMeiKanji}`;
    }
  }

  /**
   * メーター休止フラグ取得
   * @public
   * @returns {boolean}
   * @memberof Sgke340dConditionComponent
   */
  public get meterPauseFlg(): boolean {
    return this.getControl('meterPauseFlg')?.value == '1';
  }

  /**
   * メーター休止フラグ設定
   * @public
   * @param {boolean} v
   * @memberof Sgke340dConditionComponent
   */
  public set meterPauseFlg(v: boolean) {
    this.getControl('meterPauseFlg')?.setValue(v ? '1' : '0');
  }

  /**
   * メーターガス閉栓止フラグ取得
   * @public
   * @returns {boolean}
   * @memberof Sgke340dConditionComponent
   */
  public get meterGasheisenstopFlg(): boolean {
    return this.getControl('meterGasheisenstopFlg')?.value == '1';
  }

  /**
   * メーターガス閉栓止フラグ設定
   * @public
   * @param {boolean} v
   * @memberof Sgke340dConditionComponent
   */
  public set meterGasheisenstopFlg(v: boolean) {
    this.getControl('meterGasheisenstopFlg')?.setValue(v ? '1' : '0');

    // 閉止不能理由活性／非活性制御
    if (v) {
      this.getControl('shutoffFunoRsncod')?.disable();
      this.getControl('shutoffFunoRsncod')?.setValue(null);
    } else {
      this.getControl('shutoffFunoRsncod')?.enable();
    }

  }

  /**
   * 封印措置/ロック措置フラグ取得
   * @public
   * @returns {boolean}
   * @memberof Sgke340dConditionComponent
   */
  public get supplysuspensiontimeTreatmentFlg(): boolean {
    return this.getControl('supplysuspensiontimeTreatmentFlg')?.value == '1';
  }

  /**
   * 封印措置/ロック措置フラグ設定
   * @public
   * @param {boolean} v
   * @memberof Sgke340dConditionComponent
   */
  public set supplysuspensiontimeTreatmentFlg(v: boolean) {
    this.getControl('supplysuspensiontimeTreatmentFlg')?.setValue(v ? '1' : '0');
  }

  /**
   * 供給再開種別取得
   * @public
   * @returns {string}
   * @memberof Sgke340dConditionComponent
   */
  public get supplyresumptionClsfy(): string {

    if (!(this.getControl('workPlaceCode').value == '1'
      && this.getControl('supplystopResultDivcd').value == "0")) {
      // 作業場所が現地作業 かつ 作業結果が供給停止以外の場合
      return null;
    }

    // 立会要否取得
    const attendanceKahi = this.suppntInfo.attendanceKahi;

    if (this.isSimple()) {
      // メーター工事不要であること マイコンメーターであること かつ マイコン点滅なしであること
      if (attendanceKahi == '0') {
        // 立会不可
        return Sgke340dConditionComponent.QUICK_RESUME;
      } else {
        // 立会可
        return Sgke340dConditionComponent.QUICK_RESUME_NEED_CONTACT;
      }
    }
    return Sgke340dConditionComponent.SUPPLY_RESUME;
  }

  /**
   * 使用量取得
   * @public
   * @returns {string}
   * @memberof Sgke340dConditionComponent
   */
  public get duringstopEmpamt(): string {
    const value = this.getControl('duringstopEmpamt')?.value;
    return value ? this.decimalPipe?.transform(value) + '㎥' : '';
  }

  /**
   * 再開時の連絡要日付取得
   * @public
   * @returns {string}
   * @memberof Sgke340dConditionComponent
   */
  public get contactRequiredOnResumeDate(): string {
    // 作業結果
    const supplystopResultDivcd = this.getControl('supplystopResultDivcd').value;
    // 供給再開種別
    const supplyresumptionClsfy = this.getControl('supplyresumptionClsfy').value;

    if (supplystopResultDivcd == Sgke340dConditionComponent.SUPPLY_STOP_RESTART_RESULT_CODE_STOP
      && supplyresumptionClsfy == Sgke340dConditionComponent.QUICK_RESUME) {
      // 作業結果が「供給停止」かつ供給再開種別が"簡易供給再開"の場合

      // 供給停止日
      const supplystopDate = this.getControl('supplystopDate').value;

      if (supplystopDate) {
        // 再開時の連絡要日付（供給停止日より３１日後）
        let contactRequiredOnResumeDate = new Date(supplystopDate);
        contactRequiredOnResumeDate.setDate(contactRequiredOnResumeDate.getDate() + 31);

        // Date型をString型に変換
        const yyyy = String(contactRequiredOnResumeDate.getFullYear()).slice(-4).padStart(4, '0');
        const mm = String(contactRequiredOnResumeDate.getMonth() + 1).padStart(2, '0');
        const dd = String(contactRequiredOnResumeDate.getDate()).padStart(2, '0');

        return `${yyyy}${mm}${dd}`;
      }
    }
    return '';
  }

  /**
   * コンストラクタ
   * @param {UntypedFormBuilder} fb 
   * @param {FormGroupDirective} parent 
   * @param {TimeApiService} timeApiService 
   */
  constructor(
    private fb: UntypedFormBuilder,
    private parent: FormGroupDirective,
    private timeApiService: TimeApiService,
    private decimalPipe: DecimalPipe,
  ) {
  }

  /**
   * 初期処理
   * @returns {void}
   * @memberof Sgke340dConditionComponent
   */
  ngOnInit(): void {
    this.parentForm = this.parent.form;

    this.suppntInfo = this.initResponse.initialhyojiInfo.basicInfoArea.suppntInfo;
    this.clmtoInfo = this.initResponse.initialhyojiInfo.basicInfoArea.clmtoInfo;
    this.supplystopResultCont = this.initResponse.initialhyojiInfo.regInfoArea.supplystopResultCont;
    this.shishinInputCont = this.initResponse.initialhyojiInfo.regInfoArea.shishinInputCont;

    this.createForm();

    // セレクターに値をセットする
    this.microcontrollermeterFlashingList = this.getSelectBoxFromCodeConversionMst('S301191')
    this.efuInstallationList = this.getSelectBoxFromCodeConversionMst('S301231');

    // 供給再開種別設定
    this.getControl('supplyresumptionClsfy').setValue(this.supplyresumptionClsfy);

    // 作業場所変更時処理
    this.onChangeWorkPlace();
  }

  /**
   * 購読解除
   * @returns {void}
   * @memberof Sgke340dConditionComponent
   */
  ngOnDestroy(): void {
    //購読を停止する処理
    this.onDestroy$.next();
    this.parentForm.clearValidators();
    this.parentForm.updateValueAndValidity();
    this.parentForm.removeControl('supplystopResultInfo');
  }

  /**
   * マイコン点滅変更時処理
   * @public
   * @returns {void}
   * @memberof Sgke340dConditionComponent
   */
  public onChangeMicrocontrollermeterFlashing(): void {
    // 供給再開種別設定
    this.getControl('supplyresumptionClsfy').setValue(this.supplyresumptionClsfy);
  }

  /**
   * 作業場所変更時処理
   * @public
   * @returns {void}
   * @memberof Sgke340dConditionComponent
   */
  public onChangeWorkPlace(): void {
    // 作業結果を活性化
    this.getControl('supplystopResultDivcd').enable();

    // 活性／非活性制御
    this.toggleActive();

    // 作業メモを表示
    this.isSgyshMemoVisible = true;
  }

  /**
   * 作業結果変更時処理
   * @public
   * @returns {void}
   * @memberof Sgke340dConditionComponent
   */
  public onChangeSupplystopResultDivcd(): void {

    // 供給再開種別設定
    this.getControl('supplyresumptionClsfy').setValue(this.supplyresumptionClsfy);

    // 活性／非活性制御
    this.toggleActive();

  }

  /**
   * コントロール取得
   * @public
   * @param {string} ctrlName
   * @returns {UntypedFormControl}
   * @memberof Sgke340dConditionComponent
   */
  public getControl(ctrlName: string): UntypedFormControl {
    return this.regInfFormGroup.get(ctrlName) as UntypedFormControl;
  }

  /**
   * コントロール値取得
   * @public
   * @param {string} ctrlName
   * @returns {any}
   * @memberof Sgke340dConditionComponent
   */
  public getControlvalue(ctrlName: string): any {
    return this.regInfFormGroup?.get(ctrlName)?.value;
  }

  /**
   * 登録ボタンの状態返却
   * @public
   * @returns {boolean}
   */
  public isRegisterButtonDisabled(): boolean {

    if (this.regInfFormGroup.invalid) {
      return true;
    }

    if (this.getControlvalue('supplystopResultDivcd') == '0') {
      // 作業結果が供給停止の場合

      // 指針必須チェック
      if (!this.getControlvalue('duringstopShishin')) {
        return true;

      }

      // 使用量必須チェック
      if (!this.getControlvalue('duringstopEmpamt')) {
        return true;

      }

    }

    return false;

  }

  /**
   * 活性／非活性制御
   * @private
   * @returns {void}
   * @memberof Sgke340dConditionComponent
   */
  private toggleActive(): void {

    if (this.getControlvalue('workPlaceCode') == '1' && this.getControlvalue('supplystopResultDivcd') == '0') {
      // 現地作業 かつ 供給停止の場合

      this.updateValidators([
        // 供給停止日
        'supplystopDate',
        // 時刻
        'supplystopHms',
        // エフ取付
        'efuInstallationFlg',
        // 供給停止書投函場所
        'supplystopdocumentPostingLocationcode',
      ], [Validators.required]);

      this.updateValidators([
        // 停止作業日時（変更後）
        'workTimeAf',
      ], []);

    } else if (this.getControlvalue('workPlaceCode') == '2' && this.getControlvalue('supplystopResultDivcd') == '0') {
      // 遠隔作業 かつ 供給停止の場合

      this.updateValidators([
        // 供給停止日
        'supplystopDate',
        // 時刻
        'supplystopHms',
      ], [Validators.required]);

      this.updateValidators([
        // エフ取付
        'efuInstallationFlg',
        // 供給停止書投函場所
        'supplystopdocumentPostingLocationcode',
        // 停止作業日時（変更後）
        'workTimeAf',
      ], []);

    } else if (this.getControlvalue('supplystopResultDivcd') == '1') {
      // 延期の場合
      this.updateValidators([
        // 供給停止日
        'supplystopDate',
        // 時刻
        'supplystopHms',
        // エフ取付
        'efuInstallationFlg',
        // 供給停止書投函場所
        'supplystopdocumentPostingLocationcode',
      ], []);

      this.updateValidators([
        // 停止作業日時（変更後）
        'workTimeAf',
      ], [Validators.required]);
    }
    // 一括で更新
    this.updateValidity([
      // 供給停止日
      'supplystopDate',
      // 時刻
      'supplystopHms',
      // エフ取付
      'efuInstallationFlg',
      // 供給停止書投函場所
      'supplystopdocumentPostingLocationcode',
      // 停止作業日時（変更後）
      'workTimeAf',
    ]);
    this.regInfFormGroup.updateValueAndValidity();
  }

  /**
   * 各コントローラーのバリデーション変更
   * @private
   * @param {string[]} controls 
   * @param {any[]} validators 
   * @memberof Sgke340dConditionComponent
   */
  private updateValidators(controls: string[], validators: any[]) {
    controls.forEach(control => {
      this.regInfFormGroup.get(control)?.setValidators(validators);
    });
  }

  /**
   * バリデーション変更反映
   * @private
   * @param {string[]} controls 
   * @memberof Sgke340dConditionComponent
   */
  private updateValidity(controls: string[]) {
    controls.forEach(control => {
      this.regInfFormGroup.get(control)?.updateValueAndValidity();
    });
  }

  /**
   * フォーム情報作成
   * @private
   * @returns {void}
   * @memberof Sgke340dConditionComponent
   */
  private createForm(): void {

    const dateValue = this.timeApiService.convertStringToDate;
    const timeValue = this.timeApiService.convertStringToTime;

    this.regInfFormGroup = this.fb.group({
      //登録区分
      regDiv: [{ value: null, disabled: false }],
      //供給地点特定番号
      suppntSpno: [{ value: this.suppntInfo.suppntSpno, disabled: false }],
      //供給停止再開履歴番号
      supplystopresumptionRrkno:
        [{ value: this.suppntInfo.supplystopresumptionRrkno, disabled: false }],
      //供給停止再開地点情報管理_更新通番号
      supplystopresumptionPointinfomngUpdatesequencenumber:
        [{ value: this.initResponse.initialhyojiInfo.supplystopresumptionPointinfomngUpdatesequencenumber, disabled: false }],
      //督促料金_更新通番号
      pressmoneyUpdatesequencenumber:
        [{ value: this.initResponse.initialhyojiInfo.pressmoneyUpdatesequencenumber, disabled: false }],
      // 督促料金EX_更新通番号
      pressmoneyExUpdatesequencenumber:
        [{ value: this.initResponse.initialhyojiInfo.pressmoneyExUpdatesequencenumber, disabled: false }],
      // 強制停止解約予定_更新通番号
      forcestpbreakScdUpdatesequencenumber:
        [{ value: this.initResponse.initialhyojiInfo.forcestpbreakScdUpdatesequencenumber, disabled: false }],
      // 使用者お客さま漢字氏名
      userName: [{ value: this.suppntInfo.userMeiKanji, disabled: false }],
      // 請求先名
      visitName: [{ value: this.clmtoInfo.clmtoMeiKanji, disabled: false }],
      // 作業場所
      workPlaceCode: [{ value: this.supplystopResultCont.workPlaceCode ?? '1', disabled: false }, Validators.required],
      // 作業結果
      supplystopResultDivcd: [{ value: this.supplystopResultCont.supplystopresumptionResultDivcd, disabled: this.supplystopResultCont.workPlaceCode ? false : true }, Validators.required],
      // 供給停止日時
      supplystopDate: [{ value: dateValue(this.supplystopResultCont.supplystopYearmonthdate), disabled: false }, this.supplystopResultCont.supplystopresumptionResultDivcd == '0' ? Validators.required : null],
      // 時刻
      supplystopHms: [{ value: timeValue(this.supplystopResultCont.supplystopHms), disabled: false }, this.supplystopResultCont.supplystopresumptionResultDivcd == '0' ? Validators.required : null],
      // 供給停止作業担当ユーザID
      supplystopWorkchargeUserid: [{ value: this.suppntInfo.supplystopWorkchargeUserid, disabled: false }, Validators.required],
      // 供給停止作業担当者名
      supplystopWorkTantoshaname: [{ value: this.suppntInfo.supplystopWorkTantoshaname, disabled: false }, Validators.required],
      // 前回検針時指針
      preOnkenshinShishin: [{ value: this.shishinInputCont.preKenshinvalue, disabled: false }],
      // 前回検針タイミング
      preKenshinTiming: [{ value: this.shishinInputCont.preKenshinTiming, disabled: false }],
      // 前回検針時メーター番号
      preOnkenshinMeternumber: [{ value: this.shishinInputCont.preMeterNo, disabled: false }],
      // 前回検針時メーター型式
      preOnkenshinMetermodel: [{ value: this.shishinInputCont.preMeterModel, disabled: false }],
      // 前回検針時メーター能力
      preOnkenshinMeterability: [{ value: this.shishinInputCont.preMeterAbility, disabled: false }],
      // 停止時指針
      duringstopShishin: [{ value: this.supplystopResultCont.duringstopShishin, disabled: true }],
      // 停止時使用量
      duringstopEmpamt: [{ value: this.supplystopResultCont.duringstopEmpamt, disabled: true }],
      // マイコン点滅フラグ
      microcontrollermeterFlashingFlg: [{
        value: this.supplystopResultCont.microcontrollermeterFlashingFlg,
        disabled: this.suppntInfo.meterBunruiCode == Sgke340dConditionComponent.METER_BUNRUI_CODE_MCU ? false : true
      }],
      // メーター休止フラグ
      meterPauseFlg: [{
        value: this.supplystopResultCont.meterPauseFlg ?? '0',
        disabled: this.suppntInfo.meterBunruiCode == Sgke340dConditionComponent.METER_BUNRUI_CODE_MCU ? false : true
      }],
      // メータガス閉栓止フラグ
      meterGasheisenstopFlg: [{
        value: this.supplystopResultCont.meterGasheisenstopFlg ?? '0'
        , disabled: false
      }],
      // 閉止不能理由コード
      shutoffFunoRsncod: [{
        value: this.supplystopResultCont.shutoffFunoRsncod,
        disabled: this.suppntInfo.meterBunruiCode == Sgke340dConditionComponent.METER_BUNRUI_CODE_MCU ? false : true
      }],
      // 供給停止時処置フラグ（封印措置／ロック措置）
      supplysuspensiontimeTreatmentFlg: [{
        value: this.supplystopResultCont.supplysuspensiontimeTreatmentFlg ?? '0',
        disabled: false
      }],
      // エフ取付フラグ
      efuInstallationFlg: [{ value: this.supplystopResultCont.efuInstallationFlg, disabled: false },
      this.supplystopResultCont.workPlaceCode == '1' && this.supplystopResultCont.supplystopresumptionResultDivcd == '0' ? Validators.required : null],
      // 供給停止書投函場所コード
      supplystopdocumentPostingLocationcode: [{ value: this.supplystopResultCont.supplystopdocumentPostingLocationcode, disabled: false },
      this.supplystopResultCont.workPlaceCode == '1' && this.supplystopResultCont.supplystopresumptionResultDivcd == '0' ? Validators.required : null],
      // 供給再開種別
      supplyresumptionClsfy: [{ value: null, disabled: true }],
      // 作業メモ
      sgyshMemo: [{ value: this.supplystopResultCont.sgyshMemo, disabled: false }],
      // 指針入力エラーフラグ設定値不明
      shishinInputErrorflag: [{ value: null, disabled: false }],
      // メーター情報一致フラグ
      meterInfoIcchiflag: [{ value: this.shishinInputCont.meterInfoIcchiflag, disabled: false }],
      // メーター取替有無フラグ
      meterTorikaeEonflg: [{ value: this.shishinInputCont.meterTorikaeEonflg, disabled: false }],
      // 取外メーター番号
      removalMeterNo: [{ value: this.shishinInputCont.removalMeterNo, disabled: false }],
      // 取外時指針
      duringremovalShishin: [{ value: this.shishinInputCont.duringremovalShishin, disabled: false }],
      // 取付メーター番号
      installationMeterNo: [{ value: this.shishinInputCont.installationMeterNo, disabled: false }],
      // 取付時指針
      duringinstallationShishin: [{ value: this.shishinInputCont.duringinstallationShishin, disabled: false }],
      // 停止時メーター番号
      duringstopMeterNo: [{ value: this.shishinInputCont.duringstopMeterNo, disabled: false }],
      // メーター回転有無フラグ
      meterRotationEonflg: [{ value: this.shishinInputCont.meterRotationEonflg, disabled: false }],
      // 停止作業日時（変更後）
      workTimeAf: [{ value: dateValue(this.supplystopResultCont.afSupplystopPlndate), disabled: false }, this.supplystopResultCont.supplystopresumptionResultDivcd == '1' ? Validators.required : null],
    });

    this.parentForm.addControl("supplystopResultInfo", this.regInfFormGroup);
  }

  /**
   * コードIDに対応するコード変換マスタ値をSelectBoxの配列で戻す
   * ※コード値の昇順で戻す
   * @param {string} codeId
   * @return {SelectBox[]}
   * @memberof Sgke340dConditionComponent
   */
  private getSelectBoxFromCodeConversionMst(codeId: string): SelectBox[] {
    let retSelectBoxList: SelectBox[] = [];
    let codeList: CodeConversionMstDto[] = [];
    if (this.codeMap.has(codeId)) {
      codeList = this.codeMap.get(codeId);
      if (codeList) {
        codeList.forEach(data => {
          const select: SelectBox = { value: data.userCode, viewValue: data.hyojiCont };
          retSelectBoxList.push(select);
        });
      }
    }
    return retSelectBoxList;
  }

  /**
   * 簡易供給再開がチェックする
   * @returns {boolean}
   * @memberof Sgke340dConditionComponent
   */
  private isSimple(): boolean {
    // メーター工事要否コード
    const meterConstYhicode = this.suppntInfo.meterConstYhicode;
    // メーター分類コード取得
    const meterBunruiCode = this.suppntInfo.meterBunruiCode;
    // マイコン点滅取得
    const microcontrollermeterFlashingFlg = this.getControl('microcontrollermeterFlashingFlg').value;

    return (meterConstYhicode == Sgke340dConditionComponent.METER_CONST_YHICODE_HI &&
      meterBunruiCode == Sgke340dConditionComponent.METER_BUNRUI_CODE_MCU &&
      microcontrollermeterFlashingFlg == Sgke340dConditionComponent.MICROCOMPUTER_METER_BLINK_FLAG_OFF
    );
  }
}
